/**
 * DEPARTURE — THE TRUCK LEAVES (item 5).
 *
 * The moment the paper becomes real: the challan and the delivery memo are
 * ISSUED here, custody moves to the vehicle, and rent starts when the customer
 * takes it. It is the one action on this screen, and it is retry-sensitive —
 * a dropped connection must not send the same lorry out twice, so the key of a
 * timed-out attempt is kept and RESENT (DEF-088, D244).
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go } from '../hooks.js';
import { Banner, Button, Card, Count, Loading, Pill, Register, Serial, Text, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { NextStep } from '../next.js';
import { challanStanding } from '../standing.js';
import { count, date, today, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('departure')!.messages;

export function Departure({ id, session }: { id: string | null; session: Session }) {
  if (id) return <SendOne challanId={Number(id)} session={session} />;
  return <ReadyToLeave />;
}

function ReadyToLeave() {
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('challans.list', { type: 'dispatch', status: 'approved', page, pageSize: 50 }), [page], MESSAGES);
  const columns: readonly Column<{ challanId: number; number: string | null; customerName: string; siteName: string; eventDate: string; lineCount: number; vehicle: string | null; journey: string | null }>[] = [
    { key: 'number', head: 'Challan', cell: (r) => r.number ? <Serial>{r.number}</Serial> : <span className="muted">not issued</span> },
    { key: 'customer', head: 'Customer', cell: (r) => <><strong>{r.customerName}</strong><div className="muted">{r.siteName}</div></> },
    { key: 'date', head: 'Prepared', cell: (r) => date(r.eventDate) },
    { key: 'cyl', head: 'Cylinders', right: true, cell: (r) => <Count n={r.lineCount} /> },
    { key: 'vehicle', head: 'Vehicle', cell: (r) => r.vehicle ?? <span className="muted">—</span> },
    { key: 'journey', head: 'Where it stands', cell: (r) => { const s = challanStanding('dispatch', 'approved', r.journey); return <Pill tone={s.tone}>{s.word}</Pill>; } },
  ];
  return (
    <Page title="Departure" sub="Approved loads still standing at the plant.">
      <Card>
        {asked.loading ? <Loading what="approved loads" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows.filter((r) => r.journey !== 'departed')} empty="Nothing is waiting to leave."
                        onOpen={(r) => go(`#/departure/${r.challanId}`)} />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
    </Page>
  );
}

/** 10 Oct — `embedded`: the step on the challan's own page, without a page frame or the duplicate line table. */
export function SendOne({ challanId, session, embedded, onChanged }: { challanId: number; session: Session; embedded?: boolean; onChanged?: () => void }) {
  const asked = useAsk(() => api.view('challans.get', { challanId }), [challanId], MESSAGES);
  const runner = useRun(MESSAGES);
  const [departedOn, setDepartedOn] = useState(today());
  const [vehicle, setVehicle] = useState('');
  const [driver, setDriver] = useState('');

  if (asked.loading) return embedded ? <Loading what="the load" /> : <Page title="Departure"><Loading what="the load" /></Page>;
  if (asked.error) return embedded ? <Banner kind="error" title={asked.error.title} /> : <Page title="Departure"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Page>;
  const d = asked.data!;
  const c = d.challan;
  const gone = c.journey === 'departed';
  if (embedded) {
    return (
      <Card title={gone ? `This load left on ${date(c.departedOn)}` : 'Send it out'} sub={gone ? 'the challan and the delivery memo are issued and never redrawn' : 'recording the departure issues the challan and the delivery memo; holding runs from this day'}>
        {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}
        {gone ? (
          <p className="muted" style={{ margin: 0 }}>The papers it issued are listed under Papers on this page.</p>
        ) : (
          <>
            <Text name="departed-on" label="Departure date" type="date" value={departedOn} onChange={setDepartedOn} required />
            <Text name="vehicle" label="Vehicle" value={vehicle} onChange={setVehicle} placeholder={c.vehicle ?? 'e.g. WB-26-A-1234'} />
            <Text name="driver" label="Driver" value={driver} onChange={setDriver} />
            <Button kind="primary" wide disabled={!can(session, 'dispatch.depart') || runner.busy} onClick={() => { void depart(); }}>
              {runner.busy ? 'Recording the departure…' : 'Record the departure'}
            </Button>
          </>
        )}
      </Card>
    );
  }

  async function depart() {
    const ok = await runner.run((retryKey) => api.command('dispatch.depart', {
      challanId, departedOn,
      ...(vehicle.trim() || c.vehicle ? { vehicle: (vehicle.trim() || c.vehicle) as string } : {}),
      ...(driver.trim() ? { driver: driver.trim() } : {}),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { asked.reload(); onChanged?.(); }
  }

  return (
    <Page title={`Send out ${c.number ?? `the load for ${c.customerName}`}`}
          sub={`${c.customerName} · ${c.siteName} · ${count(c.lineCount)} cylinder(s)`}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}

      {gone ? (
        <NextStep kind="good" title={`This load left on ${date(c.departedOn)}.`} detail="Its challan and delivery memo are issued and cannot be redrawn. Next: confirm the delivery at the customer's gate."
                  {...(can(session, 'delivery.confirm') ? { action: { label: 'Confirm the delivery', href: `#/delivery/${challanId}` } } : {})} />
      ) : (
        <NextStep title="Recording the departure issues the challan and the delivery memo."
                  detail="The cylinders pass to the vehicle, and the customer's holding starts when they take them. Issued paper is never redrawn." />
      )}

      <div className="grid two">
        <Card title={gone ? 'It has gone' : 'Send it out'}>
          {gone ? (
            <div className="detail-grid">
              <div><div className="k">Departed</div><div className="v">{date(c.departedOn)}</div></div>
              <div><div className="k">Vehicle</div><div className="v">{c.vehicle ?? '—'}</div></div>
              <div><div className="k">Driver</div><div className="v">{c.driver ?? '—'}</div></div>
              <div><div className="k">Next</div><div className="v">
                {c.journey === 'departed'
                  ? <a href={`#/delivery/${challanId}`} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/delivery/${challanId}`); }}><strong>Confirm the delivery →</strong></a>
                  : <span className="muted">the gate has answered</span>}
              </div></div>
            </div>
          ) : (
            <>
              <Text name="departed-on" label="Departure date" type="date" value={departedOn} onChange={setDepartedOn} required />
              <Text name="vehicle" label="Vehicle" value={vehicle} onChange={setVehicle} placeholder={c.vehicle ?? 'e.g. WB-26-A-1234'} />
              <Text name="driver" label="Driver" value={driver} onChange={setDriver} />
              <Button kind="primary" wide disabled={!can(session, 'dispatch.depart') || runner.busy} onClick={() => { void depart(); }}>
                {runner.busy ? 'Recording the departure…' : 'Record the departure'}
              </Button>
            </>
          )}
          {d.documents.length ? (
            <ul style={{ marginTop: 14, paddingLeft: 18 }}>
              {d.documents.map((doc) => (
                <li key={doc.id}><a href={api.documentUrl(doc.id)} target="_blank" rel="noreferrer">{words(doc.kind)} {doc.number}</a></li>
              ))}
            </ul>
          ) : null}
        </Card>

        <Card title="What is going">
          <Register
            columns={[
              { key: 'cyl', head: 'Cylinder', cell: (l: { internalId: string }) => <Serial>{l.internalId}</Serial> },
              { key: 'serial', head: 'Serial', cell: (l: { serialNo: string }) => l.serialNo },
              { key: 'gas', head: 'Gas', cell: (l: { gasService: string }) => words(l.gasService) },
            ]}
            rows={d.lines} empty="Nothing is on this load." />
        </Card>
      </div>
    </Page>
  );
}
